import {
    personalizeSecondaryMobileNav,
    sendNavClickEventsToGTM,
} from 'root/main/components/navigation/btf/navigationMobileTrackingAndPersonalization';
import { LoggingHandler } from 'root/utils/errorReporting/LoggingHandler';

export default function NavigationMobile(els) {
    return (function () {
        const bodyEl = document.body;
        const navWrapper = els[0];

        if (!navWrapper || !bodyEl) {
            LoggingHandler().logError('Mobile navigation has not loaded', {
                searchTerm: 'mobile-nav',
                tags: ['navigation'],
                fileName: 'navigationMobile.js',
            });
            return;
        }

        // Selectors
        const overlay = navWrapper.querySelector('[data-navigation-btf-mobile=overlay]');
        const burgerBtn = navWrapper.querySelector('[data-navigation-btf-mobile=burger]');

        const primaryNav = navWrapper.querySelector(
            '[data-navigation-btf-mobile=container]'
        );
        const secondaryNav = navWrapper.querySelector(
            '[data-navigation-btf-mobile=menu-secondary]'
        );

        const languageSelectorMenu = navWrapper.querySelector(
            '[data-navigation-btf-mobile=language-selector-menu]'
        );
        const languageSelectorBtn = navWrapper.querySelector(
            '[data-navigation-btf-mobile=language-selector-button]'
        );

        // Private Methods
        const calcChildMenuHeight = (parentLi, action) => {
            // the added 1 or 2px is to account for borders on the menu headings
            const [parentHeading, childMenuContainer] = [...parentLi.children].filter(
                element =>
                    !element.classList.contains(
                        'navigation-btf-mobile__item__dividing-line'
                    )
            );

            let height;
            if (action === 'opening') {
                height = parentHeading.scrollHeight + childMenuContainer.scrollHeight + 2;
            } else {
                height = parentHeading.scrollHeight + 1;
            }

            return `${height}px`;
        };

        const calcGrandchildMenuHeight = (parentLi, targetedLi, action) => {
            // the added 1 or 2px is to account for borders on the menu headings
            const [parentHeading, childMenuContainer] = [...parentLi.children].filter(
                element =>
                    !element.classList.contains(
                        'navigation-btf-mobile__item__dividing-line'
                    )
            );
            const [childHeading] = targetedLi.children;

            let height;
            if (action === 'opening') {
                height =
                    parentHeading.scrollHeight +
                    childMenuContainer.scrollHeight +
                    targetedLi.scrollHeight -
                    childHeading.scrollHeight +
                    2;
            } else {
                height =
                    parentHeading.scrollHeight +
                    childMenuContainer.scrollHeight -
                    targetedLi.scrollHeight +
                    childHeading.scrollHeight +
                    2;
            }

            return `${height}px`;
        };

        const toggleMenuHeight = (targetedLi, options) => {
            const { openMenuOnPageLoad } = options;
            const openClass = 'navigation-btf-mobile__item--active';

            const isMenuOpen = targetedLi.classList.contains(openClass);
            const parentMenuItem = targetedLi.closest('[data-navigation-level=parent]');

            if (!isMenuOpen || openMenuOnPageLoad) {
                targetedLi.classList.add(openClass);
                targetedLi.style.height = calcChildMenuHeight(targetedLi, 'opening');

                if (parentMenuItem !== targetedLi) {
                    parentMenuItem.style.height = calcGrandchildMenuHeight(
                        parentMenuItem,
                        targetedLi,
                        'opening'
                    );
                }
            } else {
                targetedLi.classList.remove(openClass);
                targetedLi.style.height = calcChildMenuHeight(targetedLi, 'closing');

                if (parentMenuItem !== targetedLi) {
                    parentMenuItem.style.height = calcGrandchildMenuHeight(
                        parentMenuItem,
                        targetedLi,
                        'closing'
                    );
                }
            }
        };

        const initializeSubMenus = li => {
            li.style.height = `${li.scrollHeight}px`;
            const subMenu =
                li.querySelector('.navigation-btf-mobile__submenu') ??
                li.querySelector('.navigation-btf-mobile__subsubmenu');
            if (subMenu) subMenu.style.display = 'block';
        };

        const openSubMenusOnClick = (e, clickedLi) => {
            if (e.target.parentElement !== clickedLi) return;
            if (clickedLi.classList.contains('navigation-btf-mobile__item--has-subnav')) {
                e.preventDefault();
                toggleMenuHeight(clickedLi, { openMenuOnPageLoad: false });
            }
        };

        const toggleMobileNavigation = e => {
            e.preventDefault();
            if (!bodyEl.classList.contains('js-navigation-mobile-open')) {
                bodyEl.classList.add('js-navigation-mobile-open');
            } else {
                bodyEl.classList.remove('js-navigation-mobile-open');
            }
        };

        const toggleCountrySelector = e => {
            e.preventDefault();
            if (
                languageSelectorMenu &&
                !languageSelectorMenu.classList.contains('js-language-selector-open')
            ) {
                languageSelectorMenu.classList.add('js-language-selector-open');
                languageSelectorMenu.scrollIntoView({
                    behavior: 'smooth',
                });
            } else {
                languageSelectorMenu.classList.remove('js-language-selector-open');
            }
        };

        const overlayClickHandler = () => {
            if (overlay) {
                overlay.addEventListener('click', function (e) {
                    if (e.target === overlay) {
                        e.stopPropagation();
                        toggleMobileNavigation(e);
                    }
                });
            }
        };

        const firstBurgerClickAfterPageLoadHandler = menuItemWithSubnav => {
            if (burgerBtn) {
                burgerBtn.addEventListener(
                    'click',
                    function () {
                        initializeSubMenus(menuItemWithSubnav);

                        // TO DO: look into weird active/not-active class behaviour
                        /* There is some weird behaviour with the preset active/not-active class. 
                        The active class is being added in addition to the not-active class on all LI Menu Items.
                        For the matching page behaviour, only the active class is present, which means this menu 
                        *should* be open/highlighted on page load, in the mobile menu. Until this is resolved
                        this conditional is here to prevent all menus being open because they all have an active class.
                        The openMenuOnPageLoad setting is also part of this bandaid solution.
                        */
                        if (
                            menuItemWithSubnav.classList.contains(
                                'navigation-btf-mobile__item--not-active'
                            )
                        ) {
                            return;
                        }
                        toggleMenuHeight(menuItemWithSubnav, {
                            openMenuOnPageLoad: true,
                        });
                    },
                    { once: true }
                );
            }
        };

        const menuItemWithSubnavClickHandler = menuItemWithSubnav => {
            menuItemWithSubnav.querySelector('a').addEventListener('click', function (e) {
                e.preventDefault();
                openSubMenusOnClick(e, menuItemWithSubnav);
            });
        };

        // Public Method
        const init = () => {
            personalizeSecondaryMobileNav();
            sendNavClickEventsToGTM();
            overlayClickHandler();

            burgerBtn.addEventListener('click', function (e) {
                toggleMobileNavigation(e);
            });

            languageSelectorMenu &&
                languageSelectorBtn &&
                languageSelectorBtn.addEventListener('click', function (e) {
                    toggleCountrySelector(e);
                });

            if (primaryNav) {
                const menuItemsWithSubnav = primaryNav.querySelectorAll(
                    '[data-navigation-btf-mobile=has-subnav]'
                );
                if (menuItemsWithSubnav && menuItemsWithSubnav.length > 0) {
                    Array.from(menuItemsWithSubnav).forEach(menuItemWithSubnav => {
                        firstBurgerClickAfterPageLoadHandler(menuItemWithSubnav);
                        menuItemWithSubnavClickHandler(menuItemWithSubnav);
                    });
                }
            }
            if (secondaryNav) {
                const secondaryMenuItemsWithSubnav = secondaryNav.querySelectorAll(
                    '[data-navigation-btf-mobile=has-subnav]'
                );
                if (
                    secondaryMenuItemsWithSubnav &&
                    secondaryMenuItemsWithSubnav.length > 0
                ) {
                    Array.from(secondaryMenuItemsWithSubnav).forEach(
                        menuItemWithSubnav => {
                            menuItemWithSubnavClickHandler(menuItemWithSubnav);
                            initializeSubMenus(menuItemWithSubnav);
                        }
                    );
                }
            }
        };

        // New object generator
        return {
            init,
        };
    })().init();
}
